import { Link } from "@tanstack/react-router"
import { Button } from "~/components/design-system/components"
import { Tooltip } from "~/components/reusable/Tooltip"
import type { TileType } from "~/components/studio/AddTileDialog"
import {
  useConfirmTileAction,
  type TileProposal,
  type UseConfirmTileActionArgs,
} from "~/hooks/useConfirmTileAction"
import { useDownloadHakiExport, type HakiExportInput } from "~/hooks/useDownloads"
import {
  EXPORT_ACTION_KIND,
  isTileActionKind,
  type ActionResolution,
  type ExportProposalPayload,
} from "~/server/api/routers/haki/types"

interface ChatActionCardProps {
  blockId: string
  validated?: unknown
  resolution?: ActionResolution
  /** Enables persisting the outcome. Absent only on surfaces with no thread of their own. */
  threadId?: number
  /** The message this card sits in — what an `answer`-target export downloads. */
  messageId?: number
}

function isTileProposal(data: unknown): data is TileProposal {
  const kind = (data as { actionKind?: unknown } | null | undefined)?.actionKind
  return isTileActionKind(kind)
}

const TYPE_LABELS: Record<TileType, string> = {
  chart: "Chart",
  table: "Table",
  kpis: "KPIs",
  matrix: "Matrix",
  auto: "Auto (Haki picks)",
}

function exportPayload(data: unknown): ExportProposalPayload | null {
  const record = data as { actionKind?: unknown; payload?: unknown } | null | undefined
  if (record?.actionKind !== EXPORT_ACTION_KIND) return null
  const payload = record.payload as ExportProposalPayload | undefined
  return payload?.target ? payload : null
}

export default function ChatActionCard({ validated, ...rest }: ChatActionCardProps) {
  // Validate before the inner component so its hooks never run against a malformed block.
  const exportProposal = exportPayload(validated)
  if (exportProposal) {
    return (
      <ExportActionCard
        payload={exportProposal}
        messageId={rest.messageId}
        threadId={rest.threadId}
      />
    )
  }
  if (!isTileProposal(validated)) {
    return (
      <pre className="overflow-x-auto rounded-lg bg-medium p-3 text-xs">
        <code>Invalid action block</code>
      </pre>
    )
  }
  return <TileActionCard {...rest} proposal={validated} />
}

function TileActionCard(props: UseConfirmTileActionArgs) {
  const { status, isSubmitting, doneReportId, proposedReportName, wasResized, confirm, cancel } =
    useConfirmTileAction(props)

  const { proposal } = props
  const { payload } = proposal
  const isCreate = proposal.actionKind === "create_tile" || proposal.actionKind === "create_tiles"
  const isMove = proposal.actionKind === "move_tile" || proposal.actionKind === "move_tiles"
  const isBatch = proposal.actionKind === "create_tiles" || proposal.actionKind === "move_tiles"
  const batchTiles =
    proposal.actionKind === "create_tiles" || proposal.actionKind === "move_tiles"
      ? proposal.payload.tiles
      : null
  const tileCount = batchTiles?.length ?? 1
  // A move only names a destination — the tile's own fields belong to create/update proposals.
  const tileFields =
    proposal.actionKind === "create_tile" || proposal.actionKind === "update_tile"
      ? proposal.payload
      : null
  const isInboxTarget = payload.target === "inbox"
  const targetLabel = isInboxTarget ? "inbox" : "report"

  const namedReport =
    "newReportName" in payload && payload.newReportName
      ? `New report "${payload.newReportName}"`
      : proposedReportName
  const destinationLabel = isInboxTarget
    ? "Inbox pinboard"
    : isMove
      ? (namedReport ?? "Studio report")
      : isCreate
        ? namedReport
        : undefined

  const heading = isMove
    ? `Haki wants to move ${isBatch ? `${tileCount} tiles` : "this tile"} to ${destinationLabel}`
    : isCreate
      ? `Haki wants to create ${isBatch ? `${tileCount} ${targetLabel} tiles` : `${isInboxTarget ? "an" : "a"} ${targetLabel} tile`}`
      : `Haki wants to edit this ${targetLabel} tile`
  const doneText = isMove
    ? `${isBatch ? `${tileCount} tiles` : "tile"} moved${wasResized ? " and resized to fit the inbox grid" : ""}`
    : isBatch
      ? `${tileCount} ${targetLabel} tiles created`
      : `${targetLabel} tile ${isCreate ? "created" : "updated"}`

  return (
    <div className="my-2 min-w-0 rounded-lg bg-medium p-4">
      <div className="label-base mb-2">{heading}</div>
      <div className="flex flex-col gap-1 paragraph-sm text-fg-subtle">
        {destinationLabel && (
          <div>
            <span className="text-fg-muted">
              {isCreate && !isInboxTarget ? "Report:" : "Destination:"}
            </span>{" "}
            {destinationLabel}
          </div>
        )}
        {batchTiles && (
          <div>
            <span className="text-fg-muted">Tiles:</span>
            <ul className="mt-1 list-disc pl-5">
              {batchTiles.map((tile, index) => (
                <li key={"id" in tile ? tile.id : `${tile.name}-${index}`}>{tile.name}</li>
              ))}
            </ul>
          </div>
        )}
        {tileFields?.name && (
          <div>
            <span className="text-fg-muted">Name:</span> {tileFields.name}
          </div>
        )}
        {tileFields?.prompt && (
          <div>
            <span className="text-fg-muted">Question:</span> {tileFields.prompt}
          </div>
        )}
        {tileFields?.preferredType && (
          <div>
            <span className="text-fg-muted">Type:</span> {TYPE_LABELS[tileFields.preferredType]}
          </div>
        )}
      </div>
      {status === "done" ? (
        <div className="mt-3 paragraph-sm text-fg-subtle">
          Done. {doneText}.{" "}
          {isInboxTarget ? (
            <Link to="/inbox" className="hover:underline">
              Open your inbox
            </Link>
          ) : doneReportId != null ? (
            <Link
              to="/insights/studio/$reportId"
              params={{ reportId: String(doneReportId) }}
              className="hover:underline"
            >
              Open the report
            </Link>
          ) : (
            "Open the report to see it run."
          )}
        </div>
      ) : status === "cancelled" ? (
        <div className="mt-3 paragraph-sm text-fg-subtle">Cancelled.</div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button
            variant="primary"
            size="sm"
            requires="edit"
            onClick={confirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Confirm"}
          </Button>
          <Button variant="neutral" size="sm" onClick={cancel} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}

function exportTargetLabel(payload: ExportProposalPayload): string {
  if (payload.label) return payload.label
  if (payload.target === "report") return "this report"
  if (payload.target === "tile") return "this tile"
  if (payload.target === "blocks" && (payload.blockIds?.length ?? 0) > 1) {
    return `these ${payload.blockIds?.length} tables`
  }
  return "the data above"
}

function exportInput(
  payload: ExportProposalPayload,
  messageId?: number,
  threadId?: number,
): HakiExportInput | null {
  if (payload.target === "report")
    return payload.reportId != null ? { reportId: payload.reportId } : null
  if (payload.target === "tile") return payload.tileId != null ? { tileId: payload.tileId } : null
  if (payload.target === "blocks") {
    return payload.blockIds?.length && threadId != null
      ? { threadId, blockIds: payload.blockIds }
      : null
  }
  return messageId != null ? { messageId } : null
}

function ExportActionCard({
  payload,
  messageId,
  threadId,
}: {
  payload: ExportProposalPayload
  messageId?: number
  threadId?: number
}) {
  const exportToExcel = useDownloadHakiExport()

  const target = exportInput(payload, messageId, threadId)
  const input = target && { ...target, ...(payload.fileName ? { fileName: payload.fileName } : {}) }

  return (
    <div className="my-2 min-w-0 rounded-lg bg-medium p-4">
      <div className="label-base mb-2">Download {exportTargetLabel(payload)} as Excel</div>
      <div className="mt-3">
        <Tooltip
          title={input == null ? "This answer is still being saved" : "Download as an Excel file"}
        >
          <Button
            variant="primary"
            size="sm"
            onClick={() => input && exportToExcel.mutate(input)}
            disabled={input == null || exportToExcel.isPending}
          >
            {exportToExcel.isPending ? "Preparing..." : "Download .xlsx"}
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}
